import tinycolor from 'tinycolor2'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../constants/common'
import {
  THEME_CONTENT_BACKGROUND_IMAGE_REG_EXP,
  THEME_CONTENT_IMPORTANT_SUFFIX_REG_EXP,
  THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE,
  THEME_CONTENT_PAPER_COLOR,
  THEME_CONTENT_SEE_THROUGH_BACKGROUNDS,
  THEME_DARK_CONTENT_LIGHT_BACKGROUND_LUMINANCE,
  THEME_DARK_SURFACE_COLOR,
  THEME_DARK_TEXT_COLOR,
  THEME_DARK_TEXT_CONTRAST,
} from '../../constants/themeMode'
import { toDocument } from '../html'
import {
  getDarkContentBackgroundColor,
  getDarkContentTextColor,
} from '../theme'
import { isThemeDark } from '../themeMode'

function getNormalizedColorValue(value) {
  return value
    ?.trim()
    .toLowerCase()
    .replace(THEME_CONTENT_IMPORTANT_SUFFIX_REG_EXP, EMPTY_STRING)
}

// undefined: nothing declared; null: a value whose readability can't be judged
function getDeclaredColor(value) {
  const normalized = getNormalizedColorValue(value)

  if (
    normalized &&
    !THEME_CONTENT_SEE_THROUGH_BACKGROUNDS.includes(normalized)
  ) {
    const color = tinycolor(normalized)

    if (!color.isValid()) {
      return null
    }

    if (color.getAlpha() > 0) {
      return color
    }
  }
}

function getElementBackground(declared, parentBackground) {
  const opaque =
    declared.getAlpha() < 1
      ? tinycolor.mix(parentBackground, declared, declared.getAlpha() * 100)
      : declared

  if (opaque.getLuminance() <= THEME_DARK_CONTENT_LIGHT_BACKGROUND_LUMINANCE) {
    return { background: opaque.toHexString(), isReplaced: false }
  }

  return {
    background: getDarkContentBackgroundColor(opaque),
    isReplaced: true,
  }
}

function setCorrectedStyle(element, property, value, priority) {
  if (!element.hasAttribute(THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE)) {
    element.setAttribute(
      THEME_CONTENT_ORIGINAL_STYLE_ATTRIBUTE,
      element.getAttribute('style') ?? EMPTY_STRING
    )
  }

  element.style.setProperty(property, value, priority)
}

function getCorrectedBackground(
  element,
  declaredBackground,
  parentBackground,
  priority
) {
  if (declaredBackground === null) {
    return null
  }

  if (!declaredBackground || !parentBackground) {
    return parentBackground
  }

  const { background, isReplaced } = getElementBackground(
    declaredBackground,
    parentBackground
  )

  if (isReplaced) {
    setCorrectedStyle(element, 'background-color', background, priority)
  }

  return background
}

function getCorrectedColor(
  element,
  { background, color, isOwnColorContext, priority }
) {
  if (
    !isOwnColorContext ||
    !background ||
    !color ||
    tinycolor.readability(color, background) >= THEME_DARK_TEXT_CONTRAST
  ) {
    return color
  }

  const corrected = getDarkContentTextColor(color, background)

  setCorrectedStyle(element, 'color', corrected, priority)

  return tinycolor(corrected)
}

function setElementPaperStyle(
  element,
  { declaredBackground, declaredColor, priority }
) {
  if (declaredBackground === undefined) {
    setCorrectedStyle(
      element,
      'background-color',
      THEME_CONTENT_PAPER_COLOR.background,
      priority
    )
  }

  if (declaredColor === undefined) {
    setCorrectedStyle(
      element,
      'color',
      THEME_CONTENT_PAPER_COLOR.text,
      priority
    )
  }
}

function correctElementColors(
  element,
  parentBackground,
  parentColor,
  priority
) {
  const declaredBackground = getDeclaredColor(
    element.style.backgroundColor || element.getAttribute('bgcolor')
  )
  const declaredColor = getDeclaredColor(
    element.style.color || element.getAttribute('color')
  )

  if (hasThemeContentBackgroundImage(element)) {
    setElementPaperStyle(element, {
      declaredBackground,
      declaredColor,
      priority,
    })

    return
  }

  const background = getCorrectedBackground(
    element,
    declaredBackground,
    parentBackground,
    priority
  )

  const color = getCorrectedColor(element, {
    background,
    color: declaredColor === undefined ? parentColor : declaredColor,
    isOwnColorContext:
      declaredBackground !== undefined || declaredColor !== undefined,
    priority,
  })

  Array.from(element.children).forEach(child =>
    correctElementColors(child, background, color, priority)
  )
}

export function getThemeContentContainer(html) {
  return toDocument(`<div>${html}</div>`).body.firstElementChild
}

export function hasThemeContentBackgroundImage(element) {
  return (
    THEME_CONTENT_BACKGROUND_IMAGE_REG_EXP.test(
      element.style.backgroundImage
    ) || Boolean(element.getAttribute('background')?.trim())
  )
}

// The editor marks corrections with `!important` for the restore; React drops such style values
export function getDarkContentHtml(
  html,
  {
    background = THEME_DARK_SURFACE_COLOR,
    priority = EMPTY_STRING,
  } = EMPTY_OBJECT
) {
  if (!html) {
    return html
  }

  const container = getThemeContentContainer(html)

  Array.from(container.children).forEach(child =>
    correctElementColors(
      child,
      tinycolor(background).toHexString(),
      tinycolor(THEME_DARK_TEXT_COLOR),
      priority
    )
  )

  return container.innerHTML
}

export function getThemeContentHtml(html, theme) {
  return isThemeDark(theme) ? getDarkContentHtml(html) : html
}
